import { generateQuadBuffer, loadShader, setUniformVec3 } from "../../gl";
import { LoadedState } from "../../loader";
import { Camera, Light } from "../../common";
import { InstantState } from "../../sequencer";
import { TextureUniform } from "../TextureUniform";
import { Uniform } from "../Uniform";

type LocationsFor<T> = {
  [K in keyof T]: WebGLUniformLocation;
};

type CameraLocations = LocationsFor<Camera>;
type LightLocations = LocationsFor<Light>;
interface SceneLightingLocations {
  ambient: WebGLUniformLocation;
  cameraLightIntensity: WebGLUniformLocation;

  light1: LightLocations;

  fogColor: WebGLUniformLocation;
  fogDensity: WebGLUniformLocation;
}

export interface RenderInput extends InstantState {
  resolution: [number, number];
  fft: Float32Array;
}

export class SDFQuad {
  private readonly vao = this.gl.createVertexArray()!;
  private readonly vertexBuffer = generateQuadBuffer(this.gl);
  protected readonly shader: WebGLShader;

  private readonly now: Uniform<"float">;
  private readonly resolution: Uniform<"vec2">;
  private readonly sceneId: Uniform<"uint">;
  private readonly sceneProgress: Uniform<"float">;
  private readonly beat: Uniform<"float">;
  private readonly sceneBeat: Uniform<"uint">;
  private readonly sceneNow: Uniform<"float">;
  private readonly sceneVariables: Uniform<"vec4">;
  private readonly sceneVariables2: Uniform<"vec4">;

  private readonly lightingLocations: SceneLightingLocations;
  private readonly cameraLocations: CameraLocations;

  private nextTextureUnit = 0;
  private readonly textureUniforms: TextureUniform[] = [];
  private readonly fftTexture: TextureUniform;

  constructor(
    protected readonly gl: WebGL2RenderingContext,
    resources: LoadedState,
    vertexShader: string,
    fragmentShader: string,
  ) {
    const shader = loadShader(gl, vertexShader, fragmentShader, "SDF");
    this.shader = shader;
    this.vertexBuffer = generateQuadBuffer(gl);

    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    this.now = new Uniform(gl, shader, "now", "float");
    this.resolution = new Uniform(gl, shader, "resolution", "vec2");
    this.sceneId = new Uniform(gl, shader, "sceneId", "uint");
    this.sceneProgress = new Uniform(gl, shader, "sceneProgress", "float");
    this.beat = new Uniform(gl, shader, "beat", "float");
    this.sceneBeat = new Uniform(gl, shader, "sceneBeat", "uint");
    this.sceneNow = new Uniform(gl, shader, "sceneNow", "float");
    this.sceneVariables = new Uniform(gl, shader, "sceneVariables", "vec4");
    this.sceneVariables2 = new Uniform(gl, shader, "sceneVariables2", "vec4");

    this.cameraLocations = SDFQuad.getCameraLocations(gl, shader, "camera");
    this.lightingLocations = SDFQuad.getLightingLocations(gl, shader);

    this.addTextureUniform("randomTexture", resources.textures.noise);
    this.fftTexture = this.addTextureUniform(
      "fftTexture",
      this.createFftTexture(),
    );

    gl.useProgram(null);
    gl.bindTexture(gl.TEXTURE_2D, null);
  }

  private createFftTexture() {
    const gl = this.gl;
    const fftTexture = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, fftTexture);
    // Disable mipmaps
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.bindTexture(gl.TEXTURE_2D, null);
    return fftTexture;
  }

  protected addTextureUniform(name: string, texture: WebGLTexture) {
    const uniform = new TextureUniform(
      this.gl,
      this.shader,
      name,
      this.nextTextureUnit,
      texture,
    );
    this.nextTextureUnit++;
    this.textureUniforms.push(uniform);
    return uniform;
  }

  private bindAllTextureUniforms() {
    for (const uniform of this.textureUniforms) {
      uniform.bind();
    }
  }

  public render(renderInput: RenderInput) {
    const {
      now,
      resolution,
      sceneId,
      camera,
      sceneProgress,
      beat,
      sceneNow,
      nowInSceneBeats,
      lighting,
      sceneVariables,
      sceneVariables2,
      fft,
    } = renderInput;
    this.gl.bindVertexArray(this.vao);
    this.gl.useProgram(this.shader);

    this.now.update(now);
    this.resolution.update(resolution);
    this.sceneId.update(sceneId);
    this.sceneProgress.update(sceneProgress);
    this.beat.update(beat);
    this.sceneNow.update(sceneNow);
    this.sceneBeat.update(Math.floor(nowInSceneBeats));
    this.sceneVariables.update(sceneVariables);

    if (sceneVariables2) {
      this.sceneVariables2.update(sceneVariables2);
    }

    this.updateCameraUniforms(this.cameraLocations, camera);

    this.updateLightUniforms(this.lightingLocations.light1, lighting.light1);
    setUniformVec3(this.gl, this.lightingLocations.ambient, lighting.ambient);
    setUniformVec3(
      this.gl,
      this.lightingLocations.cameraLightIntensity,
      lighting.cameraLightIntensity,
    );
    setUniformVec3(this.gl, this.lightingLocations.fogColor, lighting.fogColor);
    this.gl.uniform1f(this.lightingLocations.fogDensity, lighting.fogDensity);

    this.updateUniforms(renderInput);

    // Update FFT texture
    this.fftTexture.update((gl) => {
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.R32F,
        fft.length,
        1,
        0,
        gl.RED,
        gl.FLOAT,
        fft,
      );
    });

    this.bindAllTextureUniforms();

    this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);
  }

  protected updateUniforms(input: RenderInput) {}

  public destroy() {
    this.gl.deleteBuffer(this.vertexBuffer);
    this.gl.deleteVertexArray(this.vao);
    this.gl.deleteProgram(this.shader);
  }

  private static getLightLocations(
    gl: WebGL2RenderingContext,
    shader: WebGLShader,
    field: string,
  ): LightLocations {
    return {
      position: gl.getUniformLocation(shader, `${field}.position`)!,
      intensity: gl.getUniformLocation(shader, `${field}.intensity`)!,
    };
  }

  private static getLightingLocations(
    gl: WebGL2RenderingContext,
    shader: WebGLShader,
  ): SceneLightingLocations {
    return {
      ambient: gl.getUniformLocation(shader, "lighting.ambient")!,
      cameraLightIntensity: gl.getUniformLocation(
        shader,
        "lighting.cameraLightIntensity",
      )!,
      light1: SDFQuad.getLightLocations(gl, shader, "lighting.light1"),
      fogColor: gl.getUniformLocation(shader, "lighting.fogColor")!,
      fogDensity: gl.getUniformLocation(shader, "lighting.fogDensity")!,
    };
  }

  private static getCameraLocations(
    gl: WebGL2RenderingContext,
    shader: WebGLShader,
    field: string,
  ): CameraLocations {
    return {
      position: gl.getUniformLocation(shader, `${field}.position`)!,
      target: gl.getUniformLocation(shader, `${field}.target`)!,
      rotationY: gl.getUniformLocation(shader, `${field}.rotationY`)!,
      zoom: gl.getUniformLocation(shader, `${field}.zoom`)!,
      roll: gl.getUniformLocation(shader, `${field}.roll`)!,
    };
  }

  private updateCameraUniforms(locations: CameraLocations, camera: Camera) {
    setUniformVec3(this.gl, locations.position, camera.position);
    setUniformVec3(this.gl, locations.target, camera.target);
    this.gl.uniform1f(locations.rotationY!, camera.rotationY ?? 0);
    this.gl.uniform1f(locations.zoom!, camera.zoom ?? 1);
    if (locations.roll) {
      this.gl.uniform1f(locations.roll, camera.roll ?? 0);
    }
  }

  private updateLightUniforms(locations: LightLocations, light: Light) {
    setUniformVec3(this.gl, locations.position, light.position);
    setUniformVec3(this.gl, locations.intensity, light.intensity);
  }
}
